import React, { useState, useEffect } from "react";
import { Card, Accordion, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { getRequest } from "api/axiosClient";
import { Highlighter } from "react-bootstrap-typeahead";

import { IScacCode, Ilocations } from "types/Common";
import errorToast from "helpers/errorToast";
import getLabel, { getLabelWithTranslationFallback } from "helpers/getLabel";
import { getObjectByKeys } from "helpers/commonHelpers";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Textarea from "components/atoms/Textarea/Textarea";
import { requestFormStartingPoint, getOffPlatformFieldSettings, getScacCodes } from "api";
import { useGetCommonFreightModes } from "api/queries/common/useGetCommonFreightModes";
import FormField from "components/molecules/FormField/FormField";
import { RequestFormActionType } from "pages/shipper/RequestForm/RequestFormState";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { FormStepTypes } from "constants/";
import trackingReferenceOption from "./ShipmentDefaultOptions";
import { IDropdown } from "../Schedule/Types";
import TrackingNumber from "./TrackingNumber";
import FormItemInfo from "../Common/FormItemInfo";
import SearchSelect from "../Common/SearchSelect";
import FormHr from "../Common/FormHr";

interface IScac {
  value: string;
  label: string;
}

function CreateShipment(props: any) {
  const methods = useFormContext();
  const {
    getValues,
    setValue,
    formState: { errors },
    trigger,
  } = methods;
  const intl = useIntl();
  const { dispatch } = props;
  const [searchParams] = useSearchParams();
  const { REACT_APP_CARGOBASE_BASE_URL } = import.meta.env;
  const currentActiveStep = searchParams.get("active_step");

  const trackingType = getValues("shipment.tracking_type") || "";

  // If tracking type is mbol or container, enable scac code input
  const enableScac = trackingType === "mbol" || trackingType === "container_no";

  const [paymentLocations, setPaymentLocations] = useState<Ilocations[]>([]);
  const [allProviders, setAllProviders] = useState([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [update, setUpdate] = useState(false);
  const [customLongfieldSettings, setCustomLongfieldSettings] = useState<any>(null);
  const [scacOptions, setScacOptions] = useState<IScac[]>([]);
  const [scacCodes, setScacCodes] = useState<IScacCode[]>([]);

  useEffect(() => {
    if (getValues("shipment.location")) {
      getOffPlatformFieldSettings(getValues("shipment.location"))
        .then((res) => {
          setCustomLongfieldSettings(res.data?.custom_longfield_01);
        })
        .catch(() => {});
    }
  }, [getValues("shipment.location")]);

  const getProviders = () => {
    getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/common/providers`)
      .then((res: any) => {
        if (res) {
          const providers = res.data.map((provider: any) => ({
            label: provider.company_name,
            value: provider.id,
          }));
          dispatch({ type: RequestFormActionType.PROVIDER_REQUEST_SUCCESS, payload: [providers] });
          setAllProviders(providers);
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveProvider",
            defaultMessage: "Could not retrieve providers. Please try again later.",
          }),
        ),
      );
  };

  useEffect(() => {
    getProviders();
    requestFormStartingPoint()
      .then((res) => {
        const locations = res.data.locations.filter(
          (location: Ilocations) => location.available && location.off_platform_shipments_enabled,
        );
        locations?.sort((a: Ilocations, b: Ilocations) => a?.label?.localeCompare(b.label));
        setPaymentLocations(locations);
        dispatch({ type: RequestFormActionType.PAYMENT_LOCATION_SUCCESS, payload: getObjectByKeys(locations, "value") });
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrievePaymentLocation",
            defaultMessage: "Could not retrieve payment locations. Please try again later.",
          }),
        ),
      );

    // Get SCAC codes from API and set the scacOptions state
    getScacCodes()
      .then((res) => {
        const scacCodes: IScacCode[] = res.data;
        setScacCodes(scacCodes);
        const newOptions = scacCodes.map(({ number, name }) => ({
          value: number,
          label: name,
        }));
        setScacOptions((prevOptions) => [...prevOptions, ...newOptions]);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveScacCodes",
            defaultMessage: "Could not retrieve SCAC codes. Please try again later.",
          }),
        ),
      );
  }, []);

  const { data: unmappedFreightModes = [] } = useGetCommonFreightModes({
    options: {
      onSuccess: () => dispatch({ type: RequestFormActionType.FREIGHT_MODE_REQUEST_SUCCESS, payload: [freightModes] }),
      onError: (err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveFreightModes",
            defaultMessage: "Could not retrieve freight modes. Please try again later.",
          }),
        ),
    },
  });

  const freightModes = unmappedFreightModes?.map((freight) => ({
    label: freight.value,
    value: freight.key,
  }));

  const shipmentErrors = () => {
    if (
      errors.shipment?.title ||
      errors.shipment?.freight_mode ||
      errors.shipment?.location ||
      errors.shipment?.tracking_number ||
      errors.shipment?.provider
    )
      return true;
    return false;
  };

  const handleRenderScacCode = ({ option, text }: { option: IScac; text: string }) => (
    <div className="p-1">
      <div className="font-weight-bold font-b3 mb-1">
        <Highlighter search={text}>{`${option.value}`}</Highlighter>
      </div>
      <div className="font-b3">
        <Highlighter search={text}>{option.label}</Highlighter>
      </div>
    </div>
  );

  useEffect(() => {
    if (currentActiveStep && currentActiveStep !== "shipment") trigger("shipment");
  }, [currentActiveStep]);

  return (
    <Card className="mb-3 form-container">
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={FormStepTypes.SHIPMENT}>
        <div className="d-flex align-items-center">
          <div>
            {shipmentErrors() ? (
              <ExclamationIcon className="mr-2" />
            ) : currentActiveStep !== "shipment" ? (
              <CheckIcon className="mr-2" />
            ) : null}
          </div>

          <h5>
            <FormattedMessage id="general.shipment" defaultMessage="Shipment" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === FormStepTypes.CONFIRMATION ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.SHIPMENT}>
        <Card.Body>
          <div className="flex-row mb-24">
            <div className="w-200">
              <FormItemInfo
                labelId="components.molecules.navigation.shipmentDetails"
                label="Shipment Details"
                descId="shipments.provideShipmentsDetails"
                description="Provide your shipment info"
              />
            </div>
            <div className="w-192">
              <FormField
                id="title"
                label="Shipment title"
                eventContext="Shipment Form"
                type="textinput"
                placeholder={intl.formatMessage({
                  id: "new-request.organisational-details.placeholder",
                  defaultMessage: "Please provide",
                })}
                field_id="shipment.title"
                onChange={(e) => setValue("title", e.target.value)}
              />
            </div>
            <div className="w-192">
              <SearchSelect
                options={paymentLocations}
                name="shipment.location"
                required
                label={
                  <FormattedMessage
                    id="general.paymentLocation"
                    values={{ paymentLocation: getLabel("Payment location", "capitalize") }}
                    defaultMessage="Payment location"
                  />
                }
                placeholder={intl.formatMessage({
                  id: "new-request.organisational.chooseCompany",
                  defaultMessage: "Choose payment location...",
                })}
              />
            </div>
            <div className="w-192">
              <label className="required">
                <FormattedMessage id="new-request.provider.freightModeSelection" defaultMessage="Freight mode selection" />
              </label>
              <Dropdown
                useHookForm
                name="shipment.freight_mode"
                className="schedule-dropdown"
                options={freightModes}
                required
              />
            </div>
          </div>
          <FormHr />
          <div className="flex-row mt-24">
            <div className="w-200">
              <FormItemInfo
                labelId="general.tracking"
                label="Tracking"
                descId="shipments.provideTrackingInfo"
                description="Provide your tracking info"
              />
            </div>
            <div className="w-192">
              <SearchSelect
                options={allProviders}
                name="shipment.provider"
                label={intl.formatMessage({
                  id: "general.provider",
                  defaultMessage: "Provider",
                })}
                placeholder={intl.formatMessage({
                  id: "new-shipment.shipmentDetails.chooseProvider",
                  defaultMessage: "Choose a Provider...",
                })}
              />
            </div>
            <div className="w-192">
              <label className="required">
                <FormattedMessage id="new-shipment.trackingNumberType" defaultMessage="Tracking number type" />
              </label>
              <Dropdown
                useHookForm
                name="shipment.tracking_type"
                className="schedule-dropdown"
                defaultValue={trackingReferenceOption(intl)?.[0]?.value}
                options={trackingReferenceOption(intl)}
                onChange={(item: IDropdown) => {
                  setValue("shipment.tracking_type", item.value);
                }}
                setUpdate={setUpdate}
                required
              />
            </div>

            <div className="w-192">
              <TrackingNumber
                trackingTypeKey="shipment.tracking_type"
                trackingKey="shipment.tracking_number"
                scacCodeKey="shipment.tracking_scac_code"
                otherScacCodeKey="shipment.other_tracking_scac_code"
                scacCodes={scacCodes}
                required
              />
            </div>
            {enableScac && (
              <div className="w-192">
                <SearchSelect
                  options={scacOptions}
                  name="shipment.tracking_scac_code"
                  required={false}
                  label={intl.formatMessage({
                    id: "general.scacCode",
                    defaultMessage: "SCAC Code",
                  })}
                  placeholder={intl.formatMessage({
                    id: "general.scacCode",
                    defaultMessage: "SCAC Code",
                  })}
                  filterBy={["label", "value"]}
                  handleRenderOption={handleRenderScacCode}
                />
              </div>
            )}
          </div>
          {customLongfieldSettings?.enabled && (
            <>
              <FormHr />
              <div className="flex-row mt-24 mb-3">
                <div className="w-200">
                  <div className="pl-16">
                    <Row className="mb-1">
                      <h6>
                        {getLabelWithTranslationFallback("Custom longfield 01", {
                          style: "capitalize",
                          translation: (
                            <FormattedMessage
                              id="new-request.quote-confirmation.organisation.customLongfield1"
                              defaultMessage="Custom longfield 1"
                            />
                          ),
                        })}
                      </h6>
                    </Row>
                  </div>
                </div>
                <div className="w-192">
                  <Textarea
                    key={getValues("shipment.location")}
                    name="shipment.custom_longfield_01"
                    rows={2}
                    className="text-body bg-white w-392"
                    placeholder={
                      intl.formatMessage({
                        id: "general.typeHere",
                        defaultMessage: "Type here...",
                      }) as string
                    }
                    required={customLongfieldSettings?.mandatory}
                  />
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}

export default CreateShipment;
